import React, { FC, useEffect, useState, ChangeEvent, Fragment } from 'react';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Box,
    Paper,
    TextField,
    Typography,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Grid,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import NumberOnlyTextField from 'components/number-only-text-field/number-only-text-field';
import TransportPoint from '../shared/transport-point.component';
import { transportDateHelper } from 'utils/transport-date-helper';
import moment from 'moment';
import { CONSTANTS } from 'config/constants';
import { TransportOrder, TransportServices, TransportStatus } from 'models/transport';
import TransportStatusIndicator from '../shared/transport-status-indicator.component';
import { Order } from 'models/order';
import TransportLog from './transport-log.component';
import TransportDeadline from 'components/shared/transport-deadline/transport-deadline.component';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import TransportDate from '../shared/transport-date.component';
import { patientDisplayName } from 'utils/orders';

const { TRANSPORT_DEADLINES } = CONSTANTS;

/** The editable fields of a transport, as sent to the `updateTransport` mutation. */
export type TransportOrderEdits = Partial<
    Pick<TransportOrder, 'manualPackageCount' | 'comment' | 'internalMessage' | 'deadline'>
>;

type TransportDetailsDialogProps = {
    transportOrder?: TransportOrder;
    selectedDate: Date | null;
    open: boolean;
    onClose: () => void;
    /**
     * `changes` holds only the fields the user actually edited in this dialog.
     * DD-3443: the schedule page sends exactly those, so a dialog opened from a
     * stale list can no longer reset a deadline a colleague saved in the meantime.
     */
    onOk: (transport: TransportOrder, changes: TransportOrderEdits) => void;
    onRevoke: (transportOrderId: string, selectedDate: Date | null) => void;
};

const TransportDetailsDialog: FC<TransportDetailsDialogProps> = ({
    transportOrder,
    selectedDate,
    open,
    onClose,
    onOk,
    onRevoke,
}: TransportDetailsDialogProps) => {
    const { t: translate } = useTranslation(['transport']);
    const [pickUpDate, setPickUpDate] = useState<string>('');
    const [dropoffDate, setDropoffDate] = useState<string>('');
    const [noOfManualPackages, setNoOfManualPackages] = useState<number>(0);
    const [comments, setComments] = useState<string>('');
    const [internalMessage, setInternalMessage] = useState<string>('');
    const [deadline, setDeadline] = useState<string>(TRANSPORT_DEADLINES[0]);
    const [showOpterLog, setShowOpterLog] = useState(false);
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);

    const dialogTitle = (transport?: TransportOrder) => {
        if (transport) {
            const transportType = translate(`transport:${transport.serviceCode}`);
            return transport.externalReference ? `${transportType} - ${transport.externalReference}` : transportType;
        }
        return '';
    };

    useEffect(() => {
        setShowOpterLog(false);
        if (transportOrder) {
            setPickUpDate(moment(transportOrder.transportDate).format('YYYY-MM-DD'));
            if (
                transportOrder.serviceCode === TransportServices.DELIVERY &&
                selectedDate &&
                moment(new Date(transportOrder.transportDate).setHours(0, 0, 0, 0)).isSame(
                    new Date(selectedDate).setHours(0, 0, 0, 0),
                )
            ) {
                setDropoffDate(moment(transportOrder.transportDate).format('YYYY-MM-DD'));
            } else {
                setDropoffDate(transportDateHelper.nextWorkingDayAsString(transportOrder.transportDate, true));
            }
            setNoOfManualPackages(transportOrder.manualPackageCount as number);
            setComments(transportOrder.comment || '');
            setInternalMessage(transportOrder.internalMessage || '');
            setDeadline(transportOrder.deadline);
        }
    }, [transportOrder]);

    const onPackagesChange = (packages: string) => {
        const value = packages && parseInt(packages);
        setNoOfManualPackages(value as number);
    };

    const handleCommentChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setComments(event.target.value);
    };

    const handleInternalMessageChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        setInternalMessage(event.target.value);
    };

    const collectChanges = (): TransportOrderEdits => {
        const changes: TransportOrderEdits = {};
        if (noOfManualPackages !== transportOrder?.manualPackageCount) {
            changes.manualPackageCount = noOfManualPackages;
        }
        if (comments !== (transportOrder?.comment || '')) {
            changes.comment = comments;
        }
        if (internalMessage !== (transportOrder?.internalMessage || '')) {
            changes.internalMessage = internalMessage;
        }
        if (deadline !== transportOrder?.deadline) {
            changes.deadline = deadline;
        }
        return changes;
    };

    const updateTransport = () => {
        const changes = collectChanges();
        const transportObj = Object.assign({}, transportOrder, changes);
        onOk(transportObj, changes);
    };

    const orderText = (order: Order) => {
        // DD-3620: the server withholds the patient from a restricted external
        // user, so the label is built from what arrived rather than printing
        // "undefined undefined".
        const name = patientDisplayName(order.patient);
        if (!name) return order.orderId;
        return `${order.orderId} - ${name}, ${order.patient?.personalIdentityNumber ?? ''}`;
    };

    const navigateToOrder = (order: Order) => {
        const orderUrl = `/order-details/${order._id}/main-view`;
        window.open(orderUrl, '_blank');
    };

    const hasDenthubOrders = () => Boolean(transportOrder?.orders?.length);

    // Total packages on a transport is manualPackageCount + attached Denthub orders.
    // When orders are attached they already make the transport non-empty, so the
    // manual (additional) package count may legitimately be zero.
    const hasValidPackageCount = () => {
        if (!Number.isInteger(noOfManualPackages) || noOfManualPackages < 0) {
            return false;
        }
        return hasDenthubOrders() || noOfManualPackages >= 1;
    };

    const isReadOnly = () => {
        if (transportOrder && transportOrder.status === TransportStatus.PLANNED) {
            return transportOrder.serviceCode === TransportServices.PICKUP
                ? transportDateHelper.isPickupReadOnly(transportOrder.transportDate)
                : transportDateHelper.isDeliveryReadOnly(transportOrder.transportDate);
        }
        return true;
    };

    const OrderList = ({ orders }: { orders: Order[] }) => {
        return (
            <Fragment>
                <Box mt={2}>
                    <Paper variant="outlined">
                        <Box p={1}>
                            <Typography variant="subtitle2">
                                {translate('transport:DENTHUB_ORDERS_TO_PICKUP')}
                            </Typography>
                            <List disablePadding>
                                {orders?.map((order) => (
                                    <ListItem key={order._id} disablePadding>
                                        <ListItemButton onClick={() => navigateToOrder(order)}>
                                            <ListItemText sx={{ color: 'primary.main' }} primary={orderText(order)} />
                                        </ListItemButton>
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Paper>
                </Box>
                <Box mt={2}>
                    <NumberOnlyTextField
                        inputProps={{ readOnly: isReadOnly() }}
                        onValueChange={onPackagesChange}
                        size="small"
                        fullWidth
                        label={translate('transport:NO_OF_ADDITIONAL_PACKAGES')}
                        hasDecimal={false}
                        value={noOfManualPackages}
                        helperText={translate('transport:NO_OF_ADDITIONAL_PACKAGES_HINT')}
                    />
                </Box>
            </Fragment>
        );
    };

    return (
        <Fragment>
            {transportOrder ? (
                <Fragment>
                    <Dialog fullWidth maxWidth={showOpterLog ? 'xl' : 'md'} open={open} onClose={onClose}>
                        <DialogTitle>{dialogTitle(transportOrder)}</DialogTitle>
                        <DialogContent>
                            <Grid container>
                                <Grid item xs={12} md={showOpterLog ? 5 : 12}>
                                    <Box display="flex" alignItems="center">
                                        <TransportStatusIndicator
                                            status={transportOrder?.status}
                                            hasProblem={transportOrder?.hasProblem || false}
                                            showText={true}
                                        />
                                        {transportOrder ? (
                                            <Box ml={1} flexGrow={1}>
                                                <Button size="small" onClick={() => setShowOpterLog(!showOpterLog)}>
                                                    {translate(`transport:SHOW_DETAILS`)}
                                                </Button>
                                            </Box>
                                        ) : undefined}
                                        {deadline && deadline !== 'No Deadline' ? (
                                            <Box>
                                                <Typography variant="subtitle2" color="warning.main">
                                                    {translate(`transport:DEADLINE`)} {deadline}
                                                </Typography>
                                            </Box>
                                        ) : undefined}
                                        {transportOrder.isSameDayDelivery ? (
                                            <Box ml={2}>
                                                <Typography variant="subtitle2" color="warning.main">
                                                    {translate(`transport:SAME_DAY_DELIVERY`)}
                                                </Typography>
                                            </Box>
                                        ) : undefined}
                                    </Box>
                                    <Box mt={2}>
                                        <Paper variant="outlined">
                                            <Box p={1}>
                                                {transportOrder.transportDate && (
                                                    <Box mb={1}>
                                                        <TransportDate
                                                            label={translate(`transport:TRANSPORT_DATE`)}
                                                            date={transportOrder.transportDate}
                                                        />
                                                    </Box>
                                                )}
                                                <Box mb={1}>
                                                    <TransportPoint
                                                        location={transportOrder?.pickup}
                                                        isFrom={true}
                                                        date={pickUpDate}
                                                    />
                                                </Box>
                                                <Box>
                                                    <TransportPoint
                                                        location={transportOrder?.delivery}
                                                        isFrom={false}
                                                        date={dropoffDate}
                                                    />
                                                </Box>
                                            </Box>
                                        </Paper>
                                    </Box>
                                    {transportOrder?.orders?.length ? (
                                        <OrderList orders={transportOrder.orders} />
                                    ) : undefined}
                                    <Box mt={2}>
                                        <NumberOnlyTextField
                                            inputProps={{ readOnly: isReadOnly() }}
                                            onValueChange={onPackagesChange}
                                            size="small"
                                            fullWidth
                                            label={translate('transport:NO_OF_PACKAGES')}
                                            hasDecimal={false}
                                            value={noOfManualPackages}
                                        />
                                    </Box>

                                    {transportOrder ? (
                                        <Box mt={2}>
                                            <TextField
                                                inputProps={{ readOnly: isReadOnly() }}
                                                label={translate(`transport:MESSAGE_TO_CARRIER`)}
                                                size="small"
                                                fullWidth
                                                value={comments}
                                                onChange={handleCommentChange}
                                                multiline
                                                rows={2}
                                            />
                                        </Box>
                                    ) : undefined}
                                    {transportOrder ? (
                                        <Box mt={2}>
                                            <TextField
                                                inputProps={{ readOnly: isReadOnly() }}
                                                label={translate(`transport:INTERNAL_MESSAGE`)}
                                                size="small"
                                                fullWidth
                                                value={internalMessage}
                                                onChange={handleInternalMessageChange}
                                                multiline
                                                rows={2}
                                            />
                                        </Box>
                                    ) : undefined}
                                    {internalMessage ? (
                                        <Box mt={2}>
                                            <Typography variant="subtitle2">
                                                {translate('transport:INTERNAL_MESSAGE')}
                                            </Typography>
                                            <Box>{internalMessage}</Box>
                                        </Box>
                                    ) : undefined}
                                    {transportOrder?.serviceCode === TransportServices.DELIVERY ? (
                                        <Box mt={2}>
                                            <TransportDeadline
                                                readOnly={isReadOnly()}
                                                deadline={deadline}
                                                onDeadlineChange={(newDeadline) => setDeadline(newDeadline)}
                                            />
                                        </Box>
                                    ) : undefined}
                                </Grid>
                                <Grid item xs={12} md={showOpterLog ? 7 : 12}>
                                    {showOpterLog ? (
                                        <Box ml={2}>
                                            <TransportLog opterOrderId={transportOrder?.externalReference || '0'} />
                                        </Box>
                                    ) : undefined}
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{ justifyContent: 'flex-start' }}>
                            <Box flexGrow={1}>
                                {!isReadOnly() ? (
                                    <Button color="error" onClick={() => setIsConfirmOpen(true)}>
                                        {translate('transport:CANCEL_TRANSPORT')}
                                    </Button>
                                ) : undefined}
                            </Box>
                            <Button onClick={onClose}>{translate('common:CANCEL')}</Button>
                            {!isReadOnly() ? (
                                <Button
                                    disabled={!hasValidPackageCount()}
                                    onClick={updateTransport}
                                    variant="contained"
                                >
                                    {translate('common:SAVE')}
                                </Button>
                            ) : (
                                <Button disabled={true}>{translate('transport:CAN_NOT_BE_MODIFIED')}</Button>
                            )}
                        </DialogActions>
                    </Dialog>
                    <ConfirmDialog
                        open={isConfirmOpen}
                        handleClose={() => setIsConfirmOpen(false)}
                        dialogTitle={translate('transport:CANCEL_TRANSPORT')}
                        dialogContent={
                            hasDenthubOrders()
                                ? translate('transport:CONFIRM_REVOKE_WITH_ORDERS', {
                                      orderCount: transportOrder.orders.length,
                                  })
                                : translate('transport:CONFIRM_REVOKE')
                        }
                        handleConfirm={() => {
                            setIsConfirmOpen(false);
                            if (transportOrder) {
                                onRevoke(transportOrder._id as string, selectedDate);
                            }
                        }}
                    />
                </Fragment>
            ) : undefined}
        </Fragment>
    );
};

export default TransportDetailsDialog;
